import React from 'react';
import { alpha, Box, styled } from '@mui/material';
import { Typography } from '../../../Typography';
import { SPACING, COLORS, TYPOGRAPHY, DATE_INPUT_WIDTH } from '../../constants';
import { getDateInputPlaceholder } from '../../utils/dateFormatting';

const InputFieldsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  gap: `${SPACING.lg}px`,
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
}));

const DateInputField = styled('input')<{ hasError?: boolean }>(({ theme, hasError }) => ({
  width: `${DATE_INPUT_WIDTH}px`,
  padding: `10px ${SPACING.lg}px`,
  backgroundColor: COLORS.bgPrimary,
  border: `1px solid ${hasError ? COLORS.borderError : COLORS.borderPrimary}`,
  borderRadius: '10px',
  boxShadow: `0px 1px 2px 0px ${COLORS.shadowXs}, inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05)`,
  fontFamily: 'inherit',
  fontSize: TYPOGRAPHY.textMd.fontSize,
  lineHeight: TYPOGRAPHY.textMd.lineHeight,
  fontWeight: 400,
  color: hasError ? COLORS.textError : COLORS.textPrimary,
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  position: 'relative',
  '&:focus': {
    borderColor: hasError ? COLORS.borderError : theme.palette.primary.main,
    boxShadow: hasError
      ? `0px 1px 2px 0px ${COLORS.shadowXs}, 0px 0px 0px 4px ${COLORS.shadowError}, inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05)`
      : `0px 1px 2px 0px ${COLORS.shadowXs}, 0px 0px 0px 2px ${COLORS.bgPrimary}, 0px 0px 0px 4px ${alpha(
          theme.palette.primary.main,
          0.24,
        )}, inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05)`,
  },
  '&::placeholder': {
    color: COLORS.textPlaceholder,
  },
  [theme.breakpoints.down('sm')]: {
    flex: 1,
    width: 'auto',
    minWidth: 0,
  },
}));

export type DateInputFieldsProps = {
  startInputValue: string;
  endInputValue: string;
  startInputError: boolean;
  endInputError: boolean;
  locale: string;
  t: (key: string) => string;
  onStartInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEndInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStartInputBlur: () => void;
  onEndInputBlur: () => void;
  onStartInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEndInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const DateInputFields: React.FC<DateInputFieldsProps> = ({
  startInputValue,
  endInputValue,
  startInputError,
  endInputError,
  locale,
  t,
  onStartInputChange,
  onEndInputChange,
  onStartInputBlur,
  onEndInputBlur,
  onStartInputKeyDown,
  onEndInputKeyDown,
}) => {
  return (
    <InputFieldsContainer>
      <DateInputField
        type="text"
        value={startInputValue}
        onChange={onStartInputChange}
        onBlur={onStartInputBlur}
        onKeyDown={onStartInputKeyDown}
        placeholder={getDateInputPlaceholder(locale)}
        aria-label={t('common:search_filters:date_range:start_date_input')}
        hasError={startInputError}
      />
      <Typography
        variant="body1"
        sx={{
          ...TYPOGRAPHY.textMd,
          fontWeight: 400,
          color: COLORS.textQuaternary,
        }}
      >
        –
      </Typography>
      <DateInputField
        type="text"
        value={endInputValue}
        onChange={onEndInputChange}
        onBlur={onEndInputBlur}
        onKeyDown={onEndInputKeyDown}
        placeholder={getDateInputPlaceholder(locale)}
        aria-label={t('common:search_filters:date_range:end_date_input')}
        hasError={endInputError}
      />
    </InputFieldsContainer>
  );
};
